import React, { memo, useCallback, useEffect, useMemo } from 'react'
import { ControlledHiddenInput } from 'transfix-form/components'
import ControlledSelect from 'transfix-form/components/ControlledSelect'
import { TSelectOpt } from 'transfix-ui/components/Select/SelectTypes'
import { Grid } from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton'
import { useCarrierMembershipApi } from 'apps/fleet-planner/hooks/useCarrierMembershipApi'
import { IUserMotorCarrier } from 'apps/fleet-planner/types'
import { TEST_IDS } from '../../constants'
import { useSavedLaneFormContext } from '../../context/SavedLaneFormContext'
import { getErrorMessage } from '../../schema'

export interface IMotorCarrierFormSectionProps {}

const MotorCarrierFormSection = memo<IMotorCarrierFormSectionProps>(() => {
  /**
   * Form Context
   */
  const { control, errors, setValue } = useSavedLaneFormContext()
  const motorCarrierIdErrors = getErrorMessage(errors, 'motorCarrierId')

  /**
   * Sets the value of the motorCarrierId field if the user has only one carrier
   */
  const setMotorCarrierId = useCallback(
    (motorCarriers: IUserMotorCarrier[]) => {
      /**
       * If the user is only associated to one carrier --
       * motorCarrierId field is set to the id of the first motor carrier
       */
      if (motorCarriers.length === 1) {
        const [firstMotorCarrier] = motorCarriers || []
        const { motorCarrier } = firstMotorCarrier || {}
        const { id: motorCarrierId } = motorCarrier || {}
        setValue('motorCarrierId', motorCarrierId)
      }
    },
    [setValue]
  )

  /**
   * Motor Carriers
   */
  const {
    getUserMotorCarriers,
    isUserMotorCarriersLoading: loading,
    userMotorCarriers,
  } = useCarrierMembershipApi({
    onGetUserMotorCarriersComplete: setMotorCarrierId,
  })

  const selectOptions: TSelectOpt[] = useMemo(() => {
    return userMotorCarriers.map(({ motorCarrier }) => {
      const { id: motorCarrierId, name } = motorCarrier || {}
      return {
        label: name,
        value: `${motorCarrierId}`,
      }
    })
  }, [userMotorCarriers])

  /**
   * Fetch user motor carriers when component mounts
   */
  useEffect(() => {
    getUserMotorCarriers()
  }, [getUserMotorCarriers])

  return (
    <Grid container>
      <ControlledHiddenInput control={control} name='motorCarrierId' />
      <Grid item xs={6}>
        {loading ? (
          <>
            <div className='mb1'>
              <Skeleton variant='rectangular' width='44px' height='16px' />
            </div>
            <Skeleton variant='rectangular' width='100%' height='40px' />
          </>
        ) : (
          <>
            {userMotorCarriers.length > 1 && (
              <ControlledSelect
                control={control}
                name='motorCarrierId'
                SelectProps={{
                  label: 'Carrier',
                  placeholder: 'Select carrier',
                  dataTest: TEST_IDS.motorCarrierIdSelect,
                  options: selectOptions,
                  error: !!motorCarrierIdErrors,
                  validationMessage: motorCarrierIdErrors,
                  required: true,
                  large: true,
                }}
              />
            )}
          </>
        )}
      </Grid>
    </Grid>
  )
})

export default MotorCarrierFormSection
